import { joiResolver } from "@hookform/resolvers/joi";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BiCalendar } from "react-icons/bi";
import { MdDraw, MdPalette } from "react-icons/md";
import { FaChevronDown } from "react-icons/fa6";
import { PiTelegramLogo } from "react-icons/pi";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";

import { SEND_MESSAGE_TYPE } from "../../../../constants/types";
import { CustomButton } from "../../../atoms/CustomButton";
import { ModalShell } from "../../../atoms/ModalShell";
import EmailPreviewFrame from "../../../atoms/EmailPreviewFrame";
import { RICH_TEXT_LIMITS, richTextRule } from "../../../../utils/richText";
import { HtmlEditorRef } from "../../../atoms/HtmlEditor";
import ComposeTab from "./sendPersonalMessage/ComposeTab";
import TemplateSection from "./sendPersonalMessage/TemplateSection";
import SignatureSection, {
  SignatureLimitNotice,
} from "./sendPersonalMessage/SignatureSection";
import ScheduleControl from "./shared/ScheduleControl";
import { useScheduleField } from "./shared/useScheduleField";
import { datetimeLocalSchema, scheduleJoi } from "./shared/scheduleDateTime";
import {
  cleanHtmlForEmail,
  convertMarkdownQuotesToHtml,
  resolveContentPlaceholders,
  resolveIntroPlaceholders,
} from "./sendPersonalMessage/helpers";
import {
  DEFAULT_TEMPLATE,
  TEMPLATE_KEYS,
  TEMPLATE_OPTIONS,
} from "./sendPersonalMessage/constants";
import {
  SendMessageFormValues,
  SendPersonalMessageProps,
  TemplateOptionValue,
} from "./sendPersonalMessage/types";

const SendPersonalMessage = ({
  show,
  ctrFn,
  userData,
  onResolved,
  type = SEND_MESSAGE_TYPE.SINGLE,
  recipientGroupName,
  sendContext,
}: SendPersonalMessageProps) => {
  const { t } = useTranslation();
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const signatureEditorRef = useRef<HtmlEditorRef | null>(null);
  const contentEditorRef = useRef<HtmlEditorRef | null>(null);
  const templatePanelRef = useRef<HTMLDivElement | null>(null);
  const signaturePanelRef = useRef<HTMLDivElement | null>(null);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [htmlContent, setHtmlContent] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [isCorrectingMessage, setIsCorrectingMessage] = useState(false);

  const richTextMessages = useCallback(
    (max: number, min = 0) => ({
      "richText.tooShort": t("htmlEditor.tooShort", { min }) as string,
      "richText.tooLong": t("htmlEditor.tooLongMax", { max }) as string,
      "richText.tooHeavy": t("htmlEditor.tooHeavy") as string,
    }),
    [t]
  );

  const schema = useMemo(
    () =>
      scheduleJoi
        .object<SendMessageFormValues>()
        .keys({
          template: scheduleJoi
            .string()
            .valid(
              ...TEMPLATE_OPTIONS.map(
                (option) => option.value as TemplateOptionValue
              )
            ),
          subject: scheduleJoi.string().max(100).required().label("Subject"),
          content: scheduleJoi
            .string()
            .required()
            .custom(richTextRule(RICH_TEXT_LIMITS.messageContent, 5))
            .label("Message Content")
            .messages(richTextMessages(RICH_TEXT_LIMITS.messageContent, 5)),
          sendAnotherTime: scheduleJoi.boolean(),
          message_planned_date_time: scheduleJoi
            .alternatives()
            .conditional("sendAnotherTime", {
              is: true,
              then: datetimeLocalSchema()
                .required()
                .messages({
                  "string.empty": t(
                    "dashboard.entities.viewEntityUser.sendMsg.scheduleIncompleteHint"
                  ) as string,
                  "any.required": t(
                    "dashboard.entities.viewEntityUser.sendMsg.scheduleIncompleteHint"
                  ) as string,
                  "datetimeLocal.min": t(
                    "dashboard.entities.viewEntityUser.sendSms.invalidScheduleTime"
                  ) as string,
                  "datetimeLocal.max": t(
                    "dashboard.entities.viewEntityUser.sendSms.invalidScheduleTime"
                  ) as string,
                }),
              otherwise: scheduleJoi.any().optional(),
            }),
          intro: scheduleJoi
            .string()
            .max(300)
            .optional()
            .allow(null, "")
            .label("Intro"),
          signature: scheduleJoi
            .string()
            .optional()
            .allow(null, "")
            .custom(richTextRule(RICH_TEXT_LIMITS.messageSignature))
            .label("Signature")
            .messages(richTextMessages(RICH_TEXT_LIMITS.messageSignature)),
        })
        .required(),
    [t, richTextMessages]
  );

  const methods = useForm<SendMessageFormValues>({
    mode: "onBlur",
    resolver: joiResolver(schema),
    defaultValues: {
      template: DEFAULT_TEMPLATE,
      sendAnotherTime: false,
      message_planned_date_time: null,
    },
  });

  const {
    handleSubmit,
    formState: { isValid, errors },
    reset,
    watch,
    setValue,
    control,
  } = methods;

  const isBulkSend = type !== SEND_MESSAGE_TYPE.SINGLE;

  const resolveIntroLocally =
    !isBulkSend && !sendContext.resolvesIntroServerSide;

  const { isSubmitting, isPreviewing } = sendContext;

  const sendAnotherTime = watch("sendAnotherTime");
  const messagePlannedDate = watch("message_planned_date_time");

  const schedule = useScheduleField({
    isOpen: show,
    armed: Boolean(sendAnotherTime),
    value: messagePlannedDate ?? "",
    setArmed: (armed) =>
      setValue("sendAnotherTime", armed, {
        shouldValidate: true,
        shouldDirty: false,
      }),
    setValue: (value) =>
      setValue("message_planned_date_time", value ? value : null, {
        shouldValidate: true,
        shouldDirty: false,
      }),
  });

  const isSchedulingIncomplete = schedule.isIncomplete;
  const canSubmit = isValid && !isSchedulingIncomplete && sendContext.canSend;
  const isPreviewDisabled = !isValid || isPreviewing || isSchedulingIncomplete;
  const scheduleTooltip = isSchedulingIncomplete
    ? (t(
        "dashboard.entities.viewEntityUser.sendMsg.scheduleIncompleteHint"
      ) as string)
    : undefined;

  const showSchedulePanel = schedule.armed;

  const getTemplateKey = () => {
    const currentTemplate = watch("template") || DEFAULT_TEMPLATE;
    return TEMPLATE_KEYS[currentTemplate] ?? TEMPLATE_KEYS[DEFAULT_TEMPLATE];
  };

  const handleGetHtml = () => {
    if (contentEditorRef.current) {
      const htmlContent = contentEditorRef.current.getHtml();
      if (htmlContent && htmlContent.trim()) {
        return cleanHtmlForEmail(htmlContent);
      }
    }

    const contentFromForm = watch("content");
    if (contentFromForm) {
      return cleanHtmlForEmail(contentFromForm);
    }

    return "";
  };

  const handleGetSignatureHtml = () => {
    if (signatureEditorRef.current) {
      const htmlContent = signatureEditorRef.current.getHtml();
      return cleanHtmlForEmail(htmlContent);
    }

    const signatureFromForm = watch("signature");
    if (signatureFromForm) {
      return cleanHtmlForEmail(signatureFromForm);
    }

    return "";
  };

  const handleRequestEmailView = () => {
    if (!isValid || isSchedulingIncomplete) return;

    const { subject, intro } = watch();

    const resolvedIntro = resolveIntroLocally
      ? resolveIntroPlaceholders(intro, userData)
      : intro ?? "";

    sendContext
      .preview({
        subject: resolveIntroLocally
          ? resolveIntroPlaceholders(subject, userData)
          : subject,
        contentHtml: resolveIntroLocally
          ? resolveContentPlaceholders(handleGetHtml(), userData)
          : handleGetHtml(),
        intro: resolvedIntro,
        signatureHtml: handleGetSignatureHtml(),
        template: getTemplateKey(),
      })
      .then((html) => {
        setHtmlContent(html);
        setShowEmailPreview(true);
      })
      .catch(() => {});
  };

  const onSubmit = async (values: SendMessageFormValues) => {
    const formData = new FormData();
    const cleanedHtml = handleGetHtml();

    formData.set("subject", values.subject);
    formData.set("content", cleanedHtml);
    formData.set(
      "intro",
      resolveIntroLocally
        ? resolveIntroPlaceholders(values.intro, userData)
        : values.intro ?? ""
    );
    formData.set("signature", handleGetSignatureHtml());

    const scheduledDate = schedule.getScheduledDate();
    if (scheduledDate) {
      formData.set("message_planned_date_time", scheduledDate.toUTCString());
    }

    attachments.forEach((file) => {
      formData.append("files", file);
    });

    formData.set("template", getTemplateKey());

    await sendContext.submit(formData, {
      onSuccess: () => {
        onSuccessSubmit();
        onResolved?.();
      },
    });
  };

  const onSuccessSubmit = () => {
    reset();
    ctrFn(false);
    setAttachments([]);
    setTemplateOpen(false);
    setSignatureOpen(false);
  };

  const handleTriggerFileSelection = () => {
    attachmentInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const selectedFiles = Array.from(event.target.files);
      const availableSlots = 5 - attachments.length;

      if (selectedFiles.length <= availableSlots) {
        setAttachments((prevFiles) => [...prevFiles, ...selectedFiles]);
      } else if (availableSlots > 0) {
        const filesToAdd = selectedFiles.slice(0, availableSlots);
        setAttachments((prevFiles) => [...prevFiles, ...filesToAdd]);
        toast.warning(
          `${t(
            "dashboard.entities.viewEntityUser.sendMsg.maxUploadFiles"
          )}. ${t("dashboard.entities.viewEntityUser.sendMsg.filesAdded", {
            count: availableSlots,
          })}`
        );
      } else {
        toast.error(
          t("dashboard.entities.viewEntityUser.sendMsg.maxUploadFiles")
        );
      }
    }

    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = "";
    }
  };

  const removeFile = (index: number) => {
    setAttachments((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const correct = sendContext.correct
    ? () => {
        const strategyCorrect = sendContext.correct;
        if (!strategyCorrect) return;
        const previousHtml = contentEditorRef.current?.getHtml() ?? "";
        setIsCorrectingMessage(true);
        strategyCorrect(handleGetHtml())
          .then((answer) => {
            if (contentEditorRef.current && answer) {
              const processedContent = convertMarkdownQuotesToHtml(answer);
              contentEditorRef.current.setHtml(processedContent);
              toast.success(
                ({ closeToast }) => (
                  <div className="flex items-center justify-between gap-3">
                    <span>
                      {t(
                        "dashboard.entities.viewEntityUser.sendMsg.aiImprove.applied"
                      )}
                    </span>
                    <CustomButton
                      variant="none"
                      size="xs"
                      className="font-semibold text-blue-700 underline enabled:hover:text-blue-800"
                      onClick={() => {
                        contentEditorRef.current?.setHtml(previousHtml);
                        closeToast?.();
                      }}
                      title={t("common.undo") as string}
                    />
                  </div>
                ),
                { autoClose: 8000 }
              );
            }
          })
          .catch(() => {})
          .finally(() => {
            setIsCorrectingMessage(false);
          });
      }
    : undefined;

  const saveCurrentAsDefaultIntro = sendContext.saveDefault
    ? () => sendContext.saveDefault?.("fav_intro", watch("intro") ?? "")
    : undefined;

  const saveCurrentAsDefaultSignature = sendContext.saveDefault
    ? () => sendContext.saveDefault?.("fav_signature", watch("signature") ?? "")
    : undefined;

  const saveCurrentAsDefaultTemplate = sendContext.saveDefault
    ? () => sendContext.saveDefault?.("fav_template", watch("template") ?? "")
    : undefined;

  const favorites = sendContext.favorites;

  useEffect(() => {
    if (!show) return;
    const favTemplate = favorites?.template as TemplateOptionValue | undefined;
    if (favTemplate) {
      setValue("template", favTemplate, { shouldDirty: false });
    }
    setValue("intro", favorites?.intro || "", { shouldDirty: false });
    setValue("signature", favorites?.signature || "", { shouldDirty: false });
  }, [show, favorites, setValue]);

  useEffect(() => {
    if (show) {
      setTemplateOpen(false);
      setSignatureOpen(false);
    }
  }, [show]);

  useEffect(() => {
    if (templateOpen) {
      templatePanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [templateOpen]);

  useEffect(() => {
    if (signatureOpen) {
      signaturePanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [signatureOpen]);

  const handleClose = () => {
    reset();
    setAttachments([]);
    setTemplateOpen(false);
    setSignatureOpen(false);
    ctrFn(false);
  };

  const recipientCount = sendContext.recipientCount;
  const recipientHeaderValue = (() => {
    if (type === SEND_MESSAGE_TYPE.SINGLE) {
      const first = (userData?.user_first_name as string) || "";
      const last = (userData?.user_last_name as string) || "";
      const name = `${first} ${last}`.trim();
      const email = (userData?.user_email as string) || "";
      if (name && email) {
        return (
          <>
            <span translate="no" className="font-medium text-gray-900">
              {name}
            </span>{" "}
            <span translate="no" className="text-gray-500">
              &lt;{email}&gt;
            </span>
          </>
        );
      }
      if (email) {
        return (
          <span translate="no" className="font-medium text-gray-900">
            {email}
          </span>
        );
      }
      return (
        <span className="text-gray-900">
          {t(
            "dashboard.entities.viewEntityUser.sendMsg.recipientHeader.count",
            { count: recipientCount }
          )}
        </span>
      );
    }

    if (type === SEND_MESSAGE_TYPE.MULTI) {
      return (
        <span className="text-gray-900">
          {t(
            "dashboard.entities.viewEntityUser.sendMsg.recipientHeader.selected",
            { count: recipientCount }
          )}
        </span>
      );
    }

    return (
      <span className="text-gray-900">
        {recipientGroupName
          ? t(
              "dashboard.entities.viewEntityUser.sendMsg.recipientHeader.group",
              { count: recipientCount, group: recipientGroupName }
            )
          : t(
              "dashboard.entities.viewEntityUser.sendMsg.recipientHeader.count",
              { count: recipientCount }
            )}
      </span>
    );
  })();

  const showRecipientHeader =
    type === SEND_MESSAGE_TYPE.SINGLE || recipientCount > 0;

  if (sendContext.accessDenied && show) {
    return <>{sendContext.accessDeniedModal}</>;
  }

  return (
    <FormProvider {...methods}>
      <ModalShell
        show={show}
        onClose={handleClose}
        size="7xl"
        onSubmit={handleSubmit(onSubmit)}
        title={
          t("dashboard.entities.viewEntityUser.sendMsg.modal-title") as string
        }
        footer={
          <div className="flex w-full flex-col gap-3">
            {showSchedulePanel && (
              <ScheduleControl
                id="send-schedule-panel"
                schedule={schedule}
                control={control}
                valueName="message_planned_date_time"
                error={errors.message_planned_date_time}
              />
            )}
            <div className="grid w-full grid-cols-1 items-center gap-4 sm:grid-cols-2 sm:gap-0">
              <div className="flex items-center justify-center space-x-2 sm:justify-start sm:space-x-3">
                <div
                  className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200 ${
                    canSubmit ? "bg-green-500" : "bg-amber-500"
                  }`}
                ></div>
                <div>
                  <span
                    className={`text-xs font-semibold sm:text-sm ${
                      canSubmit ? "text-green-800" : "text-amber-700"
                    }`}
                  >
                    {canSubmit
                      ? t(
                          "dashboard.entities.viewEntityUser.sendMsg.validation.readyToSend"
                        )
                      : isSchedulingIncomplete
                      ? t(
                          "dashboard.entities.viewEntityUser.sendMsg.selectDateAndTime"
                        )
                      : t(
                          "dashboard.entities.viewEntityUser.sendMsg.validation.completeFields"
                        )}
                  </span>
                  {!canSubmit && !isSchedulingIncomplete && (
                    <p className="mt-0.5 hidden text-xs text-gray-500 sm:block">
                      {t(
                        "dashboard.entities.viewEntityUser.sendMsg.validation.fillToContinue"
                      )}
                    </p>
                  )}
                  {isSchedulingIncomplete && (
                    <p className="mt-0.5 hidden text-xs text-gray-500 sm:block">
                      {scheduleTooltip}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-center space-x-2 sm:justify-end sm:space-x-3">
                <CustomButton
                  variant="outline"
                  onClick={handleRequestEmailView}
                  disabled={isPreviewDisabled}
                  title={
                    scheduleTooltip
                      ? scheduleTooltip
                      : isValid
                      ? (t(
                          "dashboard.entities.viewEntityUser.sendMsg.buttons.previewEmail"
                        ) as string)
                      : (t(
                          "dashboard.entities.viewEntityUser.sendMsg.validation.completeRequiredFields"
                        ) as string)
                  }
                  isLoading={isPreviewing}
                  comp={
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span>
                        {t(
                          "dashboard.entities.viewEntityUser.sendMsg.buttons.previewEmail"
                        )}
                      </span>
                    </div>
                  }
                />

                <CustomButton
                  variant="secondary"
                  onClick={handleClose}
                  comp={<span>{t("common.cancel")}</span>}
                  title={t("common.cancel") as string}
                />

                <div className="flex items-stretch">
                  <CustomButton
                    type="submit"
                    variant="primary"
                    isLoading={isSubmitting}
                    disabled={isSubmitting || !canSubmit}
                    className="rounded-r-none"
                    title={
                      scheduleTooltip
                        ? scheduleTooltip
                        : ((sendAnotherTime
                            ? t(
                                "dashboard.entities.viewEntityUser.sendMsg.buttons.scheduleSend"
                              )
                            : t(
                                "dashboard.entities.viewEntityUser.sendMsg.buttons.sendNow"
                              )) as string)
                    }
                    comp={
                      sendAnotherTime ? (
                        <div className="flex items-center gap-1 sm:gap-2">
                          <BiCalendar size={16} className="text-white" />
                          <span className="hidden sm:inline">
                            {t(
                              "dashboard.entities.viewEntityUser.sendMsg.buttons.scheduleSend"
                            )}
                          </span>
                          <span className="inline sm:hidden">
                            {t(
                              "dashboard.entities.viewEntityUser.sendMsg.buttons.scheduleMessageShort"
                            )}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 sm:gap-2">
                          <PiTelegramLogo
                            size={16}
                            className="sm:h-5 sm:w-5 text-white"
                          />
                          <span className="hidden sm:inline">
                            {t(
                              "dashboard.entities.viewEntityUser.sendMsg.buttons.sendNow"
                            )}
                          </span>
                          <span className="inline sm:hidden">
                            {t(
                              "dashboard.entities.viewEntityUser.sendMsg.buttons.sendMessageShort"
                            )}
                          </span>
                        </div>
                      )
                    }
                  />

                  <CustomButton
                    type="button"
                    variant="primary"
                    onClick={schedule.toggle}
                    disabled={isSubmitting}
                    className="rounded-l-none border-l border-blue-500 px-2"
                    aria-expanded={showSchedulePanel}
                    aria-controls="send-schedule-panel"
                    aria-label={
                      t(
                        "dashboard.entities.viewEntityUser.sendMsg.buttons.scheduleOption"
                      ) as string
                    }
                    tooltip={
                      t(
                        "dashboard.entities.viewEntityUser.sendMsg.buttons.scheduleOption"
                      ) as string
                    }
                    comp={
                      <FaChevronDown
                        size={14}
                        className={twMerge(
                          "text-white transition-transform",
                          showSchedulePanel && "rotate-180"
                        )}
                      />
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        }
        bodyClassName="overflow-x-hidden"
      >
        <>
          {showRecipientHeader && (
            <div className="mb-4 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-gray-100 pb-3 text-sm">
              <span className="font-medium text-gray-500">
                {t(
                  "dashboard.entities.viewEntityUser.sendMsg.recipientHeader.label"
                )}
                :
              </span>
              <span className="min-w-0 break-words">
                {recipientHeaderValue}
              </span>
            </div>
          )}

          {sendContext.banners}

          <ComposeTab
            t={t}
            type={type}
            contentEditorRef={contentEditorRef}
            attachmentInputRef={attachmentInputRef}
            attachments={attachments}
            onTriggerFileSelection={handleTriggerFileSelection}
            onFileChange={handleFileChange}
            onRemoveFile={removeFile}
            onCorrect={correct}
            isCorrectingMessage={isCorrectingMessage}
            onSaveDefaultIntro={saveCurrentAsDefaultIntro}
          />

          <div className="border-t border-gray-100" ref={templatePanelRef}>
            <button
              type="button"
              onClick={() => setTemplateOpen((open) => !open)}
              aria-expanded={templateOpen}
              aria-controls="send-template-panel"
              className="flex w-full items-center justify-between gap-3 py-4 text-left"
            >
              <span className="flex items-center gap-2">
                <MdPalette size={18} className="flex-shrink-0 text-gray-400" />
                <span className="text-lg font-semibold text-gray-900">
                  {t(
                    "dashboard.entities.viewEntityUser.sendMsg.templateToggle"
                  )}
                </span>
              </span>
              <FaChevronDown
                size={14}
                className={twMerge(
                  "flex-shrink-0 text-gray-400 transition-transform",
                  templateOpen && "rotate-180"
                )}
              />
            </button>
            {templateOpen && (
              <div id="send-template-panel" className="pb-4">
                <TemplateSection
                  t={t}
                  savedDefaultTemplate={favorites?.template}
                  onSaveDefault={saveCurrentAsDefaultTemplate}
                />
              </div>
            )}
          </div>

          <div className="border-t border-gray-100" ref={signaturePanelRef}>
            <button
              type="button"
              onClick={() => setSignatureOpen((open) => !open)}
              aria-expanded={signatureOpen}
              aria-controls="send-signature-panel"
              className="flex w-full items-center justify-between gap-3 py-4 text-left"
            >
              <span className="flex items-center gap-2">
                <MdDraw size={18} className="flex-shrink-0 text-gray-400" />
                <span className="text-lg font-semibold text-gray-900">
                  {t(
                    "dashboard.entities.viewEntityUser.sendMsg.signatureToggle"
                  )}
                </span>
              </span>
              <FaChevronDown
                size={14}
                className={twMerge(
                  "flex-shrink-0 text-gray-400 transition-transform",
                  signatureOpen && "rotate-180"
                )}
              />
            </button>
            {!signatureOpen && (
              <SignatureLimitNotice t={t} signature={watch("signature")} />
            )}
            {signatureOpen && (
              <div id="send-signature-panel" className="pb-4">
                <SignatureSection
                  t={t}
                  signatureEditorRef={signatureEditorRef}
                  savedDefaultSignature={favorites?.signature}
                  onSaveDefault={saveCurrentAsDefaultSignature}
                />
              </div>
            )}
          </div>
        </>
      </ModalShell>

      <ModalShell
        dismissible
        show={showEmailPreview}
        onClose={() => setShowEmailPreview(false)}
        size="5xl"
        title={t("dashboard.entities.viewEntityUser.sendMsg.key2")}
        bodyClassName="p-0"
      >
        <EmailPreviewFrame
          html={htmlContent}
          title={t("dashboard.entities.viewEntityUser.sendMsg.key2") as string}
        />
      </ModalShell>

      {sendContext.modals}
    </FormProvider>
  );
};

export default SendPersonalMessage;
